import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  SparkAccordion,
  SparkDynamicOutput,
  SparkImageLoader,
} from 'sharedComponents/UI';
import PromptAttachments from '../PromptAccordion/PromptAttachments';
import { useGetPromptFunctionsByID } from 'hooks/SparkBuilder/promptFunctions';
import { useAIModelByID, useAIProviders } from 'hooks/PromptFunction';
import { useSparkStepByID } from 'hooks/SparkBuilder/sparkStep';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import { useSparkOutputReferences } from 'hooks/SparkBuilder/sparkStepReferences';
import { useSparkBuilderStore } from 'stores';
import { useTransformPromptReferences } from './utils';
import { parseJSONCleaned } from 'utilities/index';
import type { AttachmentsType } from '../PromptAccordion';

function SparkPromptTestInput() {
  const transformReference = useTransformPromptReferences();
  const selectedStep = useSparkBuilderStore(
    React.useCallback((store) => store.builderSideBar.selectedStep, [])
  );
  const sparkID = useSparkBuilderStore((store) => store.sparkID);
  const { allReferencesIdMap } = useSparkOutputReferences(sparkID);

  const { data: sparkStep } = useSparkStepByID(selectedStep?.id);
  const { data: promptFunction } = useGetPromptFunctionsByID(
    sparkStep?.data.attributes.promptFunctionID
  );
  const { aiModel } = useAIModelByID(promptFunction?.data.attributes.aiModelID);
  const { providerIdMap } = useAIProviders();
  const aiProvider = providerIdMap?.[aiModel?.attributes.aiProviderID + ''];

  const inputResolved = sparkStep?.data.meta?.inputResolved;
  const { idMap } = useBoardNodeTypes();
  const outputType =
    idMap?.[promptFunction?.data.attributes.outputBoardNodeTypeID || ''];

  const fileReferences = React.useMemo(() => {
    const itemsMap = parseJSONCleaned<any[]>(
      promptFunction?.data.attributes.items || ''
    );

    if (!itemsMap || !Array.isArray(itemsMap)) return;

    return itemsMap.map((item) => allReferencesIdMap?.[item]);
  }, [promptFunction?.data.attributes.items, allReferencesIdMap]);

  const promptAttachments = React.useMemo(() => {
    if (fileReferences && fileReferences?.length > 0) {
      return fileReferences?.reduce((acc, item) => {
        const alreadyExists = acc.some(
          (attachment: any) => attachment.id === item.id
        );
        if (alreadyExists) return acc;

        const fileData = item?.value && JSON.parse(item?.value);
        if (fileData && fileData.length > 0 && fileData[0].name) {
          const fileName = fileData[0].name;
          const lastDotIndex = fileName.lastIndexOf('.');
          const fileTypeTag = fileName
            .substring(lastDotIndex + 1)
            .toLowerCase();

          acc.push({
            id: item.reference,
            title: fileName,
            type: 'memory',
            details: 'Document',
            fileTypeTag,
            thumbnail: item.iconURL,
          });
        }

        return acc;
      }, [] as AttachmentsType[]);
    }
    return [];
  }, [fileReferences]);

  return (
    <Box paddingX={2} paddingY={1}>
      <Typography fontFamily='Mont-semibold' gutterBottom>
        Prompt
      </Typography>
      {!!promptAttachments?.length && (
        <PromptAttachments
          attachments={promptAttachments}
          onAttachmentRemove={() => {}}
          containerStyle={{ paddingInline: 0 }}
        />
      )}
      <Typography gutterBottom>
        {transformReference(
          inputResolved?.prompt || promptFunction?.data.attributes.prompt || ''
        )}
      </Typography>
      {promptFunction?.data.attributes.instructions && (
        <>
          <Typography fontFamily='Mont-semibold' gutterBottom>
            Instructions
          </Typography>
          <Typography gutterBottom>
            {promptFunction?.data.attributes.instructions}
          </Typography>
        </>
      )}
      <Typography fontFamily='Mont-semibold' gutterBottom>
        Output
      </Typography>
      <Typography gutterBottom>
        {outputType?.attributes.name || 'Custom'}
      </Typography>
      {!promptFunction?.data.attributes.outputBoardNodeTypeID && (
        <>
          <Typography fontFamily='Mont-semibold' gutterBottom>
            Model
          </Typography>
          <Box display='flex' alignItems='center' gap={2}>
            <SparkImageLoader
              style={{ width: 24 }}
              src={aiProvider.attributes.iconURL}
              alt={aiProvider.attributes.name}
              loading='lazy'
            />
            <Typography>{aiModel?.attributes.name}</Typography>
          </Box>
          <SparkAccordion
            title='Config'
            accordionTitleStyles={{ fontFamily: 'Mont-semibold' }}
            summaryStyles={{ pl: 0 }}
          >
            <SparkDynamicOutput
              output={JSON.stringify(inputResolved?.executionSettings)}
            />
          </SparkAccordion>
        </>
      )}
    </Box>
  );
}

export default SparkPromptTestInput;
